import { enqueueSnackbar } from "notistack";
import React from "react";
import { useNavigate } from "react-router-dom";
import { CachePolicies } from "use-http";

import { JANZU_URL } from "config/config";
import { useAppDispatch, useAppSelector } from "hooks/store";
import { useFetchCustom } from "hooks/useFetchCustom";
import { HubWithStatus } from "models/hub";
import { Modal } from "new-components/modals/Modal";
import { Typography } from "new-components/typography/Typography";
import { setHubIsRestarting } from "store/slices/Hubs/hubsSlice";

export interface HubsRestartTimeProps {
  id: string;
  until: number;
}
interface HubRestartDialogProps {
  isDialogOpen: boolean;
  onCloseDialog: VoidFunction;
  hubName: string;
  hubId: string;
}

export function HubRestartDialog(props: HubRestartDialogProps) {
  const { isDialogOpen, onCloseDialog, hubId, hubName } = props;

  const navigate = useNavigate();

  const { currentHub } = useAppSelector((state) => state.hubs);
  const dispatch = useAppDispatch();

  const { patch: patchHub, loading: isLoading } = useFetchCustom<HubWithStatus>(
    `${JANZU_URL}reef-api/hubs/${hubId}/reboot`,
    {
      cachePolicy: CachePolicies.NO_CACHE,
      timeout: 5000,
      onTimeout: () => {
        throw new Error("Request timed out");
      },
    }
  );

  const handleClose = () => {
    if (!isLoading) onCloseDialog();
  };

  const handleRestart = async () => {
    try {
      const response = await patchHub();
      if (response) {
        const currentRestartTime = { id: hubId, until: Date.now() + 5 * 60 * 1000 };
        const restartTimesStored = localStorage.getItem("hubsRestartTime");

        if (restartTimesStored === null) localStorage.setItem("hubsRestartTime", JSON.stringify([currentRestartTime]));
        else {
          const parsedRestartTimesStored = JSON.parse(restartTimesStored);
          if (!parsedRestartTimesStored.some((elem: HubsRestartTimeProps) => elem.id === hubId)) {
            parsedRestartTimesStored.push(currentRestartTime);
            localStorage.setItem("hubsRestartTime", JSON.stringify(parsedRestartTimesStored));
          }
        }
        dispatch(setHubIsRestarting({ id: hubId, value: true }));
        if (currentHub) navigate("/reef/hubs", { replace: true });
        enqueueSnackbar("Vision Core successfully rebooted!", { variant: "success" });
        handleClose();
      } else
        enqueueSnackbar("An error occurred while trying to restart the Vision Core. Please try again", {
          variant: "error",
        });
    } catch (error) {
      throw new Error(`Invalid response from API ${error}`);
    }
  };

  return (
    <Modal
      open={isDialogOpen}
      onClose={handleClose}
      size="small"
      cancelButtonTitle="No"
      onCancel={handleClose}
      isCancelDisabled={isLoading}
      confirmationButtonTitle="Yes"
      onConfirmation={handleRestart}
      isConfirmationDisabled={isLoading}
      isConfirmationLoading={isLoading}
      body={[
        <Typography
          variant="subtitle"
          fontWeight="semibold"
          align="center"
          className="text-primary-darker sm:text-[#15121A]"
        >
          Are you sure you want to restart {hubName}?
        </Typography>,
      ]}
    />
  );
}
